import { Link } from 'react-router-dom';
import { useIdentity } from '../../lib/identity';
import { MORE_NAV, visibleItems, type NavItem } from '../../components/navItems';

/**
 * Everything that isn't one of the five bottom tabs, as full-width touch rows.
 * This screen exists so the tab bar can stay at five items with readable labels.
 *
 * Rows split into "Family" and "Grown-ups" — the same adultOnly flag that hides
 * rows from kids also draws the boundary, so the grouping can never disagree
 * with the role filter. Kids see a single ungrouped list (their whole list IS
 * the family group; a lone heading would just be noise).
 */

function Rows({ items }: { items: NavItem[] }) {
  return (
    <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface">
      {items.map((item) => (
        <li key={item.to}>
          <Link
            to={item.to}
            className="flex min-h-14 items-center gap-4 px-4 py-3 transition-colors hover:bg-surface-sunk focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-sage"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-sage-soft text-sage">
              <span className="h-5 w-5">{item.icon}</span>
            </span>
            <span className="flex-1">
              <span className="block text-base font-medium text-ink">{item.label}</span>
              {item.hint && <span className="block text-sm text-ink-soft">{item.hint}</span>}
            </span>
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4 text-ink-faint"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m9 5 7 7-7 7" />
            </svg>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function MorePage() {
  const identity = useIdentity();
  const items = visibleItems(MORE_NAV, identity.role);
  const family = items.filter((i) => !i.adultOnly);
  const grownUps = items.filter((i) => i.adultOnly);

  return (
    <div className="mx-auto max-w-lg">
      <h1 className="font-display text-2xl font-semibold text-ink">More</h1>

      {grownUps.length > 0 ? (
        <>
          <h2 className="mt-5 mb-2 text-sm font-medium text-ink-faint">Family</h2>
          <Rows items={family} />
          <h2 className="mt-6 mb-2 text-sm font-medium text-ink-faint">Grown-ups</h2>
          <Rows items={grownUps} />
        </>
      ) : (
        <div className="mt-4">
          <Rows items={family} />
        </div>
      )}
    </div>
  );
}
